import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import React from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { Controller, useForm } from 'react-hook-form';
import { useShoppingCart } from '@/context/ShoppingCartContext';
import { useTranslation } from 'react-i18next';
import { CartItemCardData } from './cartInterfaces';

interface AddOrderNameProps {
  cardData: CartItemCardData;
  isTextField: boolean;
  setIsTextField: (value: boolean) => void;
  overlap?: string;
  id: string;
  className?: string;
}

interface FormValues {
  orderName: string;
}

function AddOrderName({
  cardData,
  isTextField,
  setIsTextField,
  overlap,
  id,
  className,
}: AddOrderNameProps) {
  const { handleSubmit, control, reset, getValues } = useForm<FormValues>();
  const { updateCart } = useShoppingCart();
  const { t } = useTranslation('cart');

  const handleSubmitOrderName = (data: FormValues) => {
    const updatedOrderName = data?.orderName?.trim();
    setIsTextField(false);
    const newCardData = { ...cardData, orderName: updatedOrderName };
    overlap ? updateCart(newCardData, true, id) : updateCart(newCardData);
  };

  const handleCancel = () => {
    setIsTextField(false);
    reset();
  };

  return (
    <Box className={`mp-add-order-name d-flex justify-start mb-3 ${className ?? ''}`}>
      {isTextField ? (
        <form onSubmit={handleSubmit(handleSubmitOrderName)}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            gap={0.5}
          >
            <Controller
              control={control}
              name="orderName"
              rules={{
                required: true,
                validate: value => value?.trim() !== '',
              }}
              defaultValue={cardData?.orderName || ''}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  hiddenLabel
                  slotProps={{
                    htmlInput: {
                      'aria-label': 'add-order-name-textbox',
                    },
                  }}
                  id="filled-hidden-label-small"
                  variant="filled"
                  size="small"
                  error={!!error}
                  helperText={error ? 'Order name is required' : ''}
                />
              )}
            />
            <Box display="flex" flexDirection="row">
              <IconButton
                name="Save Edit Order Name"
                aria-label="save-edit-order-name"
                className="edit-btn"
                size="small"
                onClick={handleSubmit(handleSubmitOrderName)}
              >
                <CheckCircleRoundedIcon />
              </IconButton>
              <IconButton
                aria-label="cancel-edit-order-name"
                name="Cancel Edit Order Name"
                className="edit-btn"
                size="small"
                onClick={handleCancel}
              >
                <CancelRoundedIcon />
              </IconButton>
            </Box>
          </Box>
        </form>
      ) : (
        <>
          <Typography variant="body2" className="edit-wrap">
            {cardData?.orderName ? cardData?.orderName : t('addOrderName')}{' '}
            <Button
              onClick={() => setIsTextField(true)}
              className="cart-preview-btn p-0 order-name-edit"
              aria-label="add-order-name"
            >
              <i className="icon-edit"></i>
            </Button>
          </Typography>
        </>
      )}
    </Box>
  );
}

export default AddOrderName;
